"use client";
import React from "react";

import { Box, BoxSx, IconNameType, Target } from "../../foundations";
import { Icon } from "../../foundations/Icon/Icon";
import { Text } from "../../foundations/Typography/Text";
import { useId } from "../../hooks/useId";
import { useWarning } from "../../hooks/useWarning";

export type TabItem = React.ComponentProps<typeof Target> & {
  id: string;
  label: string;
  icon?: IconNameType;
};

export interface TabsProps {
  items: TabItem[];
  defaultActive?: string;
  alignment?: "horizontal" | "vertical";
  baseline?: boolean;
  size?: "medium" | "large";
  onClickTab: (tabId: string) => void;
}

export const Tabs: React.FC<TabsProps> = ({
  items,
  defaultActive,
  alignment = "horizontal",
  baseline = true,
  size = "medium",
  onClickTab
}) => {
  const colorClassName = `tab${useId().replace(/^[A-Z0-9]gi/, "")}`;
  const { warn } = useWarning();
  if (items.length === 0) {
    warn("Tabs requires at least one tab item in items prop");
  }

  const initialTab = items.find((item) => item.id === defaultActive)
    ? defaultActive
    : items[0]?.id;

  const [activeTab, setActiveTab] = React.useState(initialTab);

  const onSetActiveTab = React.useCallback(
    (tabId: string) => {
      setActiveTab(tabId);
      onClickTab(tabId);
    },
    [onClickTab]
  );

  const containerStyles = React.useMemo<BoxSx>(() => {
    if (alignment === "horizontal") {
      return {
        alignItems: "center",
        px: size === "medium" ? "spacing-3" : "spacing-4",
        paddingTop: "spacing-2",
        borderBottom: baseline ? "1px solid" : "unset",
        borderColor: baseline ? "stroke-neutral-tertiary" : "unset"
      };
    } else {
      return {
        alignItems: "flex-start",
        py: "spacing-3",
        paddingRight: "spacing-4",
        borderLeft: baseline ? "1px solid" : "unset",
        borderColor: baseline ? "stroke-neutral-tertiary" : "unset"
      };
    }
  }, [alignment, baseline, size]);

  const getColorStyles = React.useCallback<
    (isActive: boolean, isDisabled: boolean) => BoxSx
  >(
    (isActive, isDisabled) => {
      return {
        [`.${colorClassName}`]: {
          borderColor: isActive ? "tab-text-selected" : "transparent",
          color: isDisabled
            ? "tab-text-disabled"
            : isActive
              ? "tab-text-selected"
              : "tab-text-default",
          transition: "transition-color-medium"
        },
        ...(!isDisabled &&
          !isActive && {
            [`:hover .${colorClassName}`]: {
              borderColor: "tab-stroke-hover"
            },
            [`:active .${colorClassName}`]: {
              borderColor: "tab-stroke-click"
            }
          })
      };
    },
    [colorClassName]
  );

  const getAlignmentStyles = React.useCallback<(isActive: boolean) => BoxSx>(
    (isActive) => {
      if (alignment === "horizontal") {
        return {
          px: "spacing-2",
          py: size === "medium" ? "spacing-4" : "spacing-5",
          marginBottom: "-1px",
          borderBottom: isActive ? "3px solid" : "3px solid transparent",
          transition: "transition-color-medium"
        };
      } else {
        return {
          px: size === "medium" ? "spacing-3" : "spacing-4",
          py: size === "medium" ? "spacing-2" : "spacing-3",
          marginLeft: "-1px",
          borderLeft: isActive ? "3px solid" : "3px solid transparent",
          transition: "transition-color-medium"
        };
      }
    },
    [alignment, size]
  );

  const tabFocusStyles: BoxSx = React.useMemo(() => {
    return {
      ":focus-visible": {
        outline: "2px solid",
        outlineColor: "focus",
        borderRadius: "radius-1"
      }
    };
  }, []);

  return (
    <Box
      role="tablist"
      aria-orientation={alignment}
      sx={{
        display: "flex",
        flexDirection: alignment === "horizontal" ? "row" : "column"
      }}
    >
      {items.map((item) => {
        const isActive = item.id === activeTab;
        const isDisabled = item.disabled ? item.disabled : false;
        return (
          <Target
            {...item}
            key={item.id}
            role="tab"
            aria-selected={isActive}
            disabled={isDisabled}
            onClick={() => onSetActiveTab(item.id)}
            sx={{
              ...getColorStyles(isActive, isDisabled),
              ...tabFocusStyles
            }}
          >
            <Box sx={containerStyles}>
              <Box
                className={colorClassName}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "spacing-2",
                  ...getAlignmentStyles(isActive)
                }}
              >
                {item.icon && (
                  <Icon
                    color="currentColor"
                    size={size === "medium" ? "xs" : "sm"}
                    icon={item.icon}
                  />
                )}
                <Text
                  variant={size === "medium" ? "title-1-medium" : "h5"}
                  sx={{ color: "currentColor" }}
                >
                  {item.label}
                </Text>
              </Box>
            </Box>
          </Target>
        );
      })}
    </Box>
  );
};
